import React, { useState } from "react";
import Icon from "./icons";
import { changesLabel, layerRowLabel, mapChanges, offeredChanges } from "../../../lib/artemis/present";

// What a reply changed on the user's map (WEB-7093), as a card rather than a sentence. Layer
// changes were made by the app, so each has Undo (and Redo once undone). Saved waypoints and
// shapes have Show and Remove when their step carried a Result (API-3638).

// A saved reply's map changes made elsewhere (a partner's map, or before this page loaded), with Apply:
// onApply(change) makes the same change here and resolves with whether it did. applyTo names where.
const OfferedChanges = ({ changes, onApply, applyTo }) => {
  const [applied, setApplied] = useState({})
  return (
    <section className="artemis-changes" aria-label="Map changes from this reply">
      <div className="artemis-changes-header">
        <Icon name="map" size={15} />
        <span className="artemis-changes-title">Artemis changed the asker’s map</span>
      </div>
      {changes.map(change => (
        <div key={change.key} className="artemis-change-row">
          <span className="artemis-change-icon"><Icon name={change.icon} size={18} /></span>
          <span className="artemis-change-text">
            <span className="artemis-change-label">{change.label}</span>
            <span className="artemis-change-sub">{applied[change.key] === false ? 'Couldn’t do that here' : applyTo}</span>
          </span>
          <button type="button" className="artemis-small-button artemis-small-button-go" disabled={applied[change.key] === true}
            onClick={() => setApplied(a => ({ ...a, [change.key]: !!onApply(change) }))}>
            {applied[change.key] === true ? 'Done' : change.button}
          </button>
        </div>
      ))}
    </section>
  )
}

const MapChanges = ({ message, onToggleLayer, onUpgrade, onShowObject, onRemoveObject, onToggleView, onApply, applyTo = 'On your map' }) => {
  const { rows, notes, active } = mapChanges(message)
  const offered = onApply && message.status !== 'streaming' ? offeredChanges(message) : []
  if (rows.length === 0 && notes.length === 0) return offered.length ? <OfferedChanges changes={offered} onApply={onApply} applyTo={applyTo} /> : null

  const undoable = rows.filter(r => r.kind === 'layer' && r.on === r.asked)
  const done = message.status !== 'streaming'

  return (
    <section className="artemis-changes" aria-label="Changes to your map">
      {rows.length > 0 && (
        <div className="artemis-changes-header">
          <Icon name="map" size={15} />
          <span className="artemis-changes-title">{changesLabel(active)}</span>
          {undoable.length > 1 && (
            <button type="button" className="artemis-small-button"
              onClick={() => undoable.forEach(row => onToggleLayer(row.action, !row.on))}>
              Undo all
            </button>
          )}
        </div>
      )}
      {rows.map(row => {
        if (row.kind === 'layer') {
          const standing = row.on === row.asked
          return (
            <div key={row.key} className="artemis-change-row">
              <span className="artemis-change-icon"><Icon name="layer" size={18} /></span>
              <span className="artemis-change-text">
                <span className={`artemis-change-label ${standing ? '' : 'artemis-muted'}`}>{layerRowLabel(row)}</span>
                <span className="artemis-change-sub">{standing ? 'Layer' : 'Back as it was'}</span>
              </span>
              <button type="button" className="artemis-small-button" onClick={() => onToggleLayer(row.action, !row.on)}>
                {standing ? 'Undo' : 'Redo'}
              </button>
            </div>
          )
        }
        if (row.kind === 'view') {
          return (
            <div key={row.key} className="artemis-change-row">
              <span className="artemis-change-icon"><Icon name="map" size={18} /></span>
              <span className="artemis-change-text">
                <span className={`artemis-change-label ${row.undone ? 'artemis-muted' : ''}`}>{row.label}</span>
                <span className="artemis-change-sub">{row.sub}</span>
              </span>
              {row.canUndo && onToggleView && (
                <button type="button" className="artemis-small-button" onClick={() => onToggleView(row.action, !row.undone)}>
                  {row.undone ? 'Redo' : 'Undo'}
                </button>
              )}
            </div>
          )
        }
        if (row.kind === 'failed') {
          return (
            <div key={row.key} className="artemis-change-row artemis-change-failed">
              <span className="artemis-change-icon"><Icon name={row.upgrade ? 'lock' : 'failed'} size={18} /></span>
              <span className="artemis-change-text">
                <span className="artemis-change-label">{row.label}</span>
                <span className="artemis-change-sub">{row.sub}</span>
              </span>
              {row.upgrade && onUpgrade && (
                <button type="button" className="artemis-small-button artemis-small-button-warn" onClick={onUpgrade}>Upgrade</button>
              )}
            </div>
          )
        }
        return (
          <div key={row.key} className="artemis-change-row">
            <span className="artemis-change-icon"><Icon name={row.icon} size={18} /></span>
            <span className="artemis-change-text">
              <span className={`artemis-change-label ${row.removed ? 'artemis-muted' : ''}`}>{row.label}</span>
              <span className="artemis-change-sub">{row.sub}</span>
            </span>
            {row.canShow && onShowObject && (
              <button type="button" className="artemis-small-button artemis-small-button-go" onClick={() => onShowObject(row.object)}>Show</button>
            )}
            {row.canRemove && onRemoveObject && (
              <button type="button" className="artemis-icon-button artemis-change-remove" title="Remove from your map"
                aria-label={`Remove ${row.object.name} from your map`} onClick={() => onRemoveObject(row.object)}>
                <Icon name="trash" size={15} />
              </button>
            )}
          </div>
        )
      })}
      {done && notes.map((note, i) => <div key={i} className="artemis-changes-note">{note}</div>)}
    </section>
  )
}

export default MapChanges
